'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the File delivery service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new FileDeliveryService object.
   *
   * Argument 'fileStorageUrl' - The URL to the file delivery service in the Content REST API.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new FileDeliveryService object.
   */
  atex.aceui.service.FileDeliveryService = function(fileStorageUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    var baseUrl = function(alias) {
      return fileStorageUrl + "/alias/" + alias;
    };

    var versionBaseUrl = function(version) {
      return fileStorageUrl + "/version/" + version;
    };

    return {
      /**
       * Returns a file delivery service base url for the content represented by the given alias.
       *
       * @param {string} alias - the alias of the content to get the base url for
       *
       * @returns {Promise.<{string}, atex.aceui.error.Error>} - the content base URL
       */
      getContentBaseUrl: function(alias) {
        return q.when(baseUrl(alias));
      },

      /**
       * Returns a file delivery service base url for the content version represented by the given version.
       *
       * @param {string} version - the version of the content to get the base url for
       *
       * @returns {Promise.<{string}, atex.aceui.error.Error>} - the content base URL
       */
      getContentVersionBaseUrl: function(version) {
        return q.when(versionBaseUrl(version));
      },

      /**
       * Returns the content of the file (as text) represented by the alias and fileName properties
       * of the given input object.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       *
       * @param {string} transferInfo.alias - the alias of the content to retrieve the file from. Can not be used in
       * combination with the transferInfo.version parameter.
       *
       * @param {string} transferInfo.version - the version of the content to retrieve the file from. Can not be used
       * in combination with the transferInfo.alias parameter.
       *
       * @param {string] transferInfo.fileName - the file name of the file to retrieve
       * @param {string} transferInfo.authToken - the authorization token to use for the user data request
       *
       * @typedef {Object} TextFileResult - an object containing the result of the file request
       * @property {string} text - the content of the retrieved file
       *
       * @returns {Promise.<TextFileResult, atex.aceui.error.Error>}
       */
      getTextFile: function(transferInfo) {
        var deferred = q.defer();

        let base = (typeof transferInfo.version !== 'undefined')
          ? versionBaseUrl(transferInfo.version)
          : baseUrl(transferInfo.alias);

        var fullUrl = base + '/' + transferInfo.fileName;

        $.ajax(fullUrl, {
          'type': 'GET',
          'dataType': 'text',

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }
          },

          'success': function(data, textStatus, jqXHR) {
            deferred.resolve({ text: data });
          },

          'error': function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.FileDeliveryService', {
              404: "File '" + transferInfo.fileName + "' + was not found on content '" + transferInfo.alias + "'!",
              'default': "There was an error while retrieving file '" + transferInfo.fileName + "' + from content '" + transferInfo.alias + "'!"
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    }
  };
})(jQuery);
